import React, {createContext, useContext, useEffect, useMemo} from 'react';

interface ClassContentReady {
  /** A body tab calls this when its own data has painted. */
  onReady: () => void;
  /** True once the whole page (banner + content) is ready — both the header and the tab reveal on it. */
  revealed: boolean;
}

// The class shell wires this up so the header and the body tab reveal on the SAME signal, instead of
// each racing its own query (the header was gated on content, but the body wasn't gated on the
// banner, so a tab whose data beat the banner would paint under a skeleton header) (#19132).
const ClassContentReadyContext = createContext<ClassContentReady>({
  onReady: () => {},
  revealed: true
});

export const ClassContentReadyProvider = ({
  onReady,
  revealed,
  children
}: {
  onReady: () => void;
  revealed: boolean;
  children: React.ReactNode;
}) => {
  const value = useMemo(() => ({onReady, revealed}), [onReady, revealed]);
  return (
    <ClassContentReadyContext.Provider value={value}>{children}</ClassContentReadyContext.Provider>
  );
};

/** A body tab reports when its data is ready; the shell folds it into `revealed`. */
export const useReportClassContentReady = (isReady: boolean): void => {
  const {onReady} = useContext(ClassContentReadyContext);
  useEffect(() => {
    // Notifying the shell that async data finished loading is a real side effect, not derived state.
    // oxlint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (isReady) {
      onReady();
    }
  }, [isReady, onReady]);
};

/** A body tab shows its skeleton until this is true, so it reveals in lockstep with the header. */
export const useClassContentRevealed = (): boolean => useContext(ClassContentReadyContext).revealed;
